import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  URL_LANG_PARAM,
  normalizeLocale,
  resolvePreferredLocale,
  type AppLocale,
} from '@gift-i18n';

const BUNDLED_NAMESPACES = [
  'common',
  'nav',
  'auth',
  'dashboard',
  'settings',
  'usage',
  'subscription',
  'payments',
  'admin',
  'adminSessions',
  'adminProjects',
  'adminCredits',
  'adminModelMappings',
  'adminApiProviders',
  'adminResourcePlans',
  'adminCreditPlans',
  'adminCoupons',
  'adminTemplates',
  'templateMarketplace',
  'projects',
  'databases',
  'session',
  'sessions',
] as const;
type BundledNamespace = (typeof BUNDLED_NAMESPACES)[number];

type LocaleModule = { default: Record<string, unknown> };
const bundledLocaleModules = import.meta.glob<LocaleModule>('@gift-i18n-locales/web/*/*.json');
const bundledLocaleModuleKeys = Object.keys(bundledLocaleModules);

const loadBundledNamespace = async (locale: string, namespace: string) => {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  const normalizedNamespace = (BUNDLED_NAMESPACES.includes(namespace as BundledNamespace) ? namespace : 'common') as BundledNamespace;
  const suffix = `/web/${normalizedLocale}/${normalizedNamespace}.json`;
  const key = bundledLocaleModuleKeys.find((candidate) => candidate.endsWith(suffix));
  const loader = key ? bundledLocaleModules[key] : undefined;
  if (!loader) {
    throw new Error(`Missing bundled locale module for suffix: ${suffix}`);
  }
  return loader();
};

export function detectInitialLocale(): AppLocale {
  return resolvePreferredLocale({
    search: window.location.search,
    storage: window.localStorage,
    languages: navigator.languages,
    fallback: DEFAULT_LOCALE,
  });
}

export const initialLocale = detectInitialLocale();

void i18n
  .use(resourcesToBackend(loadBundledNamespace))
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: ['common', 'nav'],
    defaultNS: 'common',
    fallbackNS: 'common',
    load: 'currentOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: URL_LANG_PARAM,
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => normalizeLocale(lng) || DEFAULT_LOCALE,
    },
    react: { useSuspense: false },
    parseMissingKeyHandler: (key) => key,
  });

void i18n.loadNamespaces(BUNDLED_NAMESPACES as unknown as string[]);
i18n.on('failedLoading', (lng, ns, message) => {
  console.warn('[i18n:web] failed to load translation namespace', { lng, ns, message });
});
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = normalizeLocale(lng) || DEFAULT_LOCALE;
});
document.documentElement.lang = initialLocale;

export default i18n;
